import { useTranslation } from 'react-i18next';
import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import dayjs from 'dayjs';
import cx from 'clsx';

import { IconMinted } from '@/assets/badge';
import { BadgeAvatar, NavLink } from '@/components';
import { formatStreamUsd } from '@/utils';

import { OfficialTag } from '../../components/OfficialTag';
import { useBadgeDetail, useIsOfficialBadge } from '../../hooks';
import { OfficialBadge } from '../../types';
import { useBadgeMintedCount } from '../../state';
import styles from './index.module.scss';

export const DetailContainer = ({ id }: { id: string }) => {
  const { t } = useTranslation();

  const { data, isLoading } = useBadgeDetail(id);
  const [badgeCount] = useBadgeMintedCount();
  const isOfficialBadge = useIsOfficialBadge();

  const fields = useMemo(
    () => [
      {
        label: t('DetailContainer.nthpai'),
        value: data?.mint_time
          ? `${dayjs.unix(data?.mint_time).utc().format('YYYY/MM/DD')} (UTC 0)`
          : '-',
      },
      { label: t('DetailContainer.1uvk6lu'), value: formatStreamUsd(data?.tvo) },
      { label: t('DetailContainer.1epf82v'), value: formatStreamUsd(data?.vo_median) },
      { label: t('DetailContainer.e6s58s'), value: formatStreamUsd(data?.vo_mean) },
    ],
    [data?.mint_time, data?.tvo, data?.vo_mean, data?.vo_median, t],
  );

  return (
    <>
      {isLoading ? (
        <HeaderSkeleton />
      ) : (
        <div className={styles.headerContainer}>
          <div className={styles.badgeAvatarContainer}>
            {isLoading ? (
              <Skeleton width={108} height={108} className={styles.badgeLogo} />
            ) : (
              <BadgeAvatar src={data?.logo_url} className={styles.badgeLogo} width={108} />
            )}
          </div>
          <div className={styles.content}>
            <div className={styles.name}>{data?.name}</div>
            {isOfficialBadge && data && (
              <OfficialTag
                officialId={(data as OfficialBadge)?.official_id}
                className={styles.officialTag}
              />
            )}
            <div className={cx(styles.desc, isOfficialBadge && styles.isOfficial)}>
              {data?.desc}
            </div>
            <div className={styles.mintedCount}>
              <IconMinted className={styles.mintedIcon} />
              {t('DetailContainer.1247zq9')}
              {badgeCount || data?._total_supply}
            </div>
          </div>
        </div>
      )}
      <div className={styles.fields}>
        {fields.map(x => (
          <div key={x.label} className={styles.field}>
            <div className={styles.label}>{x.label}</div>
            {isLoading ? (
              <Skeleton width={90} height={17} className={styles.name} />
            ) : (
              <div className={styles.value}>{x.value}</div>
            )}
          </div>
        ))}
      </div>
      <div className={styles.footer}>
        <p>
          {t('DetailContainer.176dtgl')}{' '}
          <NavLink to={'/'} className={styles.link}>
            debank.com
          </NavLink>
        </p>
        <p>{t('DetailContainer.1gxhnwv')}</p>
      </div>
    </>
  );
};

const HeaderSkeleton = () => {
  return (
    <div className={styles.headerContainer}>
      <div className={styles.badgeAvatarContainer}>
        <Skeleton width={108} height={108} className={styles.badgeLogo} />
      </div>
      <div className={styles.content}>
        <Skeleton width={190} height={24} className={styles.name} />
        <Skeleton width={190} height={24} className={styles.desc} />
        <div className={styles.mintedCount}>
          <Skeleton width={80} height={24} className={styles.desc} />
        </div>
      </div>
    </div>
  );
};
